import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import { updatePageSEO, createBreadcrumbSchema, createPageSchema, cleanupDynamicSchemas } from '../../utils/seo';

export default function DisclaimerPage() {
  useEffect(() => {
    // SEO設定
    updatePageSEO({
      title: '免責事項｜永山の勉強アーカイブ - サイト利用に関する注意事項',
      description: '永山の勉強アーカイブの免責事項。サイトの利用に関する注意事項、コンテンツの正確性、リンク先の責任範囲について説明しています。個人の学習記録として作成された情報の取り扱いについてご確認ください。',
      keywords: '免責事項,利用規約,永山の勉強アーカイブ,注意事項',
      canonical: 'https://nagayama-study-archive.readdy.ai/disclaimer',
      ogType: 'website',
    });

    createBreadcrumbSchema([
      { name: 'ホーム', url: 'https://nagayama-study-archive.readdy.ai/' },
      { name: '免責事項', url: 'https://nagayama-study-archive.readdy.ai/disclaimer' },
    ]);

    createPageSchema({
      name: '免責事項',
      description: 'サイトの利用に関する注意事項、コンテンツの正確性、リンク先の責任範囲について説明しています。',
      url: 'https://nagayama-study-archive.readdy.ai/disclaimer',
      type: 'WebPage',
    });

    return () => {
      cleanupDynamicSchemas();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-16 sm:py-20 bg-gradient-to-r from-teal-600 to-blue-600 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <FadeInSection>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                <strong>免責事項</strong>
              </h1>
              <p className="text-base sm:text-lg text-teal-50 max-w-2xl mx-auto">
                サイト利用に関する注意事項
              </p>
            </FadeInSection>
          </div>
        </section>

        {/* Content */}
        <FadeInSection>
          <section className="py-16">
            <div className="max-w-3xl mx-auto px-6">
              <div className="prose prose-gray max-w-none">
                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">1. 当サイトの情報について</h2>
                  <p className="text-gray-600 leading-relaxed">
                    永山の勉強アーカイブ（以下「当サイト」）に掲載されている情報は、個人の学習記録として作成されたものです。情報の正確性、完全性、有用性について保証するものではありません。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">2. 損害等の責任について</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトに掲載された情報を利用することで生じた損害について、当サイト運営者は一切の責任を負いません。情報のご利用は、ご自身の判断と責任においてお願いいたします。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">3. 掲載情報の変更・削除</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトに掲載されている情報は、予告なく変更または削除される場合があります。あらかじめご了承ください。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">4. リンク先について</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトからリンクやバナーなどによって他のサイトに移動された場合、移動先サイトで提供される情報、サービス等について一切の責任を負いません。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">5. 著作権について</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトに掲載されているコンテンツ（文章、画像、動画等）の著作権は、当サイト運営者または正当な権利を有する第三者に帰属します。無断での複製、転載、改変等はお控えください。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">6. 学習内容の正確性について</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトに掲載されている学習内容は、個人の理解に基づいて記録されたものです。試験対策や実務での利用にあたっては、必ず公式の教材や専門家の意見をご確認ください。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">7. 免責事項の変更</h2>
                  <p className="text-gray-600 leading-relaxed">
                    当サイトは、必要に応じて本免責事項を変更することがあります。変更後の免責事項は、当サイトに掲載した時点から効力を生じるものとします。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">8. お問い合わせ</h2>
                  <p className="text-gray-600 leading-relaxed">
                    本免責事項に関するお問い合わせは、<a href="/contact" className="text-teal-600 hover:underline">お問い合わせフォーム</a>よりご連絡ください。
                  </p>
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>
      </main>

      <Footer />
    </div>
  );
}
